import { useQuery } from '@tanstack/react-query';
import { portfolioService } from '@/services/portfolio.service';

// Query keys for consistent cache management
export const portfolioQueryKeys = {
  all: ['portfolio'] as const,
  summary: () => [...portfolioQueryKeys.all, 'summary'] as const,
  riskMetrics: () => [...portfolioQueryKeys.all, 'risk-metrics'] as const,
  valuationReports: () =>
    [...portfolioQueryKeys.all, 'valuation-reports'] as const,
};

// Hook for portfolio summary
export function usePortfolioSummary() {
  return useQuery({
    queryKey: portfolioQueryKeys.summary(),
    queryFn: portfolioService.getPortfolioSummary,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    refetchOnWindowFocus: false,
  });
}

// Hook for portfolio risk metrics
export function usePortfolioRiskMetrics() {
  return useQuery({
    queryKey: portfolioQueryKeys.riskMetrics(),
    queryFn: portfolioService.getPortfolioRiskMetrics,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    refetchOnWindowFocus: false,
  });
}

// Hook for valuation reports
export function useValuationReports() {
  return useQuery({
    queryKey: portfolioQueryKeys.valuationReports(),
    queryFn: portfolioService.getValuationReports,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    refetchOnWindowFocus: false,
  });
}

// Combined hook for all portfolio data
export function usePortfolioData() {
  const summaryQuery = usePortfolioSummary();
  const riskMetricsQuery = usePortfolioRiskMetrics();
  const valuationReportsQuery = useValuationReports();

  const isLoading =
    summaryQuery.isLoading ||
    riskMetricsQuery.isLoading ||
    valuationReportsQuery.isLoading;
  const isError =
    summaryQuery.isError ||
    riskMetricsQuery.isError ||
    valuationReportsQuery.isError;
  const error =
    summaryQuery.error || riskMetricsQuery.error || valuationReportsQuery.error;

  // Refetch all data
  const refetch = () => {
    summaryQuery.refetch();
    riskMetricsQuery.refetch();
    valuationReportsQuery.refetch();
  };

  return {
    // Data
    portfolioSummary: summaryQuery.data,
    riskMetrics: riskMetricsQuery.data,
    valuationReports: valuationReportsQuery.data,

    // Loading states
    isLoading,
    isSummaryLoading: summaryQuery.isLoading,
    isRiskMetricsLoading: riskMetricsQuery.isLoading,
    isValuationReportsLoading: valuationReportsQuery.isLoading,

    // Error states
    isError,
    error,
    summaryError: summaryQuery.error,
    riskMetricsError: riskMetricsQuery.error,
    valuationReportsError: valuationReportsQuery.error,

    // Refetch functions
    refetch,
    refetchSummary: summaryQuery.refetch,
    refetchRiskMetrics: riskMetricsQuery.refetch,
    refetchValuationReports: valuationReportsQuery.refetch,

    // Individual query states
    summaryQuery,
    riskMetricsQuery,
    valuationReportsQuery,
  };
}

// Hook for portfolio metadata derived from valuation reports
export function usePortfolioMetadata() {
  const {
    data: valuationReports,
    isLoading: isValuationReportsLoading,
    error: valuationReportsError,
  } = useValuationReports();

  const metadata = valuationReports
    ? {
        source: 'Valuation Reports',
        total_loans: valuationReports.length,
        analysis_date:
          valuationReports[0]?.valuation_date ||
          new Date().toISOString().split('T')[0],
        description:
          'Complete loan pricing engine output results from RPX loan portfolio analysis',
        last_materialized_update: new Date().toISOString(),
      }
    : null;

  return {
    metadata,
    isLoading: isValuationReportsLoading,
    isError: !!valuationReportsError,
    error: valuationReportsError,
  };
}
